import { motion, AnimatePresence } from 'framer-motion';
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { AlertCircle, CheckCircle, Info, X } from 'lucide-react';
import { create } from 'zustand';

// Define toast types
export const TOAST_TYPES = {
  SUCCESS: 'success',
  ERROR: 'error',
  WARNING: 'warning',
  INFO: 'info',
};

// Create a Zustand store for global toast state
export const useToastStore = create((set) => ({
  toasts: [],
  addToast: (message, type = TOAST_TYPES.INFO, duration = 30000) => {
    const id = Date.now().toString();
    set((state) => ({
      toasts: [...state.toasts, { id, message, type, duration }],
    }));

    // Auto-remove toast after duration
    if (duration !== 0) {
      setTimeout(() => {
        set((state) => ({
          toasts: state.toasts.filter((toast) => toast.id !== id),
        }));
      }, duration);
    }

    return id;
  },
  removeToast: (id) => {
    set((state) => ({
      toasts: state.toasts.filter((toast) => toast.id !== id),
    }));
  },
  clearToasts: () => set({ toasts: [] }),
}));

/**
 * Single Toast component
 */
const ToastItem = ({ toast, onClose }) => {
  // Determine which icon to show based on the toast type
  const getIcon = () => {
    switch (toast.type) {
      case TOAST_TYPES.SUCCESS:
        return <CheckCircle className="tw-w-5 tw-h-5 tw-text-emerald-500" />;
      case TOAST_TYPES.ERROR:
        return <AlertCircle className="tw-w-5 tw-h-5 tw-text-red-500" />;
      case TOAST_TYPES.WARNING:
        return <AlertCircle className="tw-w-5 tw-h-5 tw-text-amber-500" />;
      case TOAST_TYPES.INFO:
      default:
        return <Info className="tw-w-5 tw-h-5 tw-text-blue-500" />;
    }
  };

  // Get background color based on type
  const getBgColor = () => {
    switch (toast.type) {
      case TOAST_TYPES.SUCCESS:
        return 'tw-bg-emerald-50';
      case TOAST_TYPES.ERROR:
        return 'tw-bg-red-50';
      case TOAST_TYPES.WARNING:
        return 'tw-bg-amber-50';
      case TOAST_TYPES.INFO:
      default:
        return 'tw-bg-blue-50';
    }
  };

  // Get border color based on type
  const getBorderColor = () => {
    switch (toast.type) {
      case TOAST_TYPES.SUCCESS:
        return 'tw-border-emerald-200';
      case TOAST_TYPES.ERROR:
        return 'tw-border-red-200';
      case TOAST_TYPES.WARNING:
        return 'tw-border-amber-200';
      case TOAST_TYPES.INFO:
      default:
        return 'tw-border-blue-200';
    }
  };

  return (
    <motion.div
      key={toast.id}
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 50 }}
      className="tw-mb-2"
    >
      <div
        className={`${getBgColor()} ${getBorderColor()} tw-border tw-rounded-lg tw-shadow-lg tw-p-4 tw-flex tw-items-center tw-space-x-3`}
      >
        {getIcon()}
        <p className="tw-text-gray-700 tw-text-sm tw-flex-grow tw-text-center">
          {toast.message}
        </p>
        <button
          onClick={() => onClose(toast.id)}
          className="tw-p-1 hover:tw-bg-gray-100 tw-rounded-full tw-flex-shrink-0"
        >
          <X className="tw-w-4 tw-h-4 tw-text-gray-500" />
        </button>
      </div>
    </motion.div>
  );
};

/**
 * Toast Container component that displays all active toasts
 */
export const ToastContainer = () => {
  const { toasts, removeToast } = useToastStore();
  const [isMounted, setIsMounted] = useState(false);

  // SSR compatibility
  useEffect(() => {
    setIsMounted(true);
    return () => setIsMounted(false);
  }, []);

  if (!isMounted) return null;

  return createPortal(
    <div className="tw-fixed tw-bottom-4 tw-left-1/2 -tw-translate-x-1/2 tw-z-[10000] tw-flex tw-flex-col tw-items-center">
      <AnimatePresence>
        {toasts.map((toast) => (
          <ToastItem key={toast.id} toast={toast} onClose={removeToast} />
        ))}
      </AnimatePresence>
    </div>,
    document.body
  );
};

/**
 * Helper hooks and functions for showing toasts
 */
export const useToast = () => {
  const { addToast, removeToast, clearToasts } = useToastStore();

  return {
    showToast: (message, type, duration) => addToast(message, type, duration),
    hideToast: removeToast,
    clearToasts,
  };
};

// Utility functions for easier usage
export const toast = {
  show: (message, type, duration) =>
    useToastStore.getState().addToast(message, type, duration),
  success: (message, duration) =>
    useToastStore.getState().addToast(message, TOAST_TYPES.SUCCESS, duration),
  error: (message, duration) =>
    useToastStore.getState().addToast(message, TOAST_TYPES.ERROR, duration),
  warning: (message, duration) =>
    useToastStore.getState().addToast(message, TOAST_TYPES.WARNING, duration),
  info: (message, duration) =>
    useToastStore.getState().addToast(message, TOAST_TYPES.INFO, duration),
  clear: () => useToastStore.getState().clearToasts(),
};

// Export the toast utility as default
export default toast;
